import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, FileText, Headphones, Wrench, Gauge, Crown, Play, Presentation, Loader2, Star } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { BrandLink } from "@/components/Brand";
import { toast } from "sonner";

const ICONS = { "welcome-pack": BookOpen, "role-advisor": Headphones, "role-technician": Wrench, "role-controller": Gauge, "role-manager": Crown };

const openDoc = async (key) => {
  try {
    const r = await fetch(`${API}/help/docs/${key}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error();
    const url = URL.createObjectURL(await r.blob());
    window.open(url, "_blank", "noopener");
  } catch { toast.error("Couldn't open that document"); }
};

export default function HelpPage() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    api.get("/help/docs").then((r) => setDocs(r.data.docs)).catch(() => setDocs([]));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-outline" data-testid="help-back-btn"><ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span></Link>
            <BrandLink />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        <div>
          <div className="label-overline">Help &amp; training</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2">Everything on <span className="text-primary">one page</span> each.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Your role card is first. Print it, stick it on the desk or the workshop wall. The welcome pack has the whole first week.</p>
        </div>

        {docs === null ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="help-docs-grid">
            {docs.map((d) => {
              const Icon = ICONS[d.key] || FileText;
              return (
                <button key={d.key} type="button" onClick={() => openDoc(d.key)} disabled={!d.available}
                  className={`card-default p-5 text-left hover:-translate-y-0.5 transition-transform flex items-start gap-4 disabled:opacity-50 ${d.recommended ? "border-primary/60 ring-1 ring-primary/30" : ""}`}
                  data-testid={`help-doc-${d.key}`}>
                  <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="w-5 h-5" /></span>
                  <span>
                    <span className="flex items-center gap-2 font-bold">{d.title}{d.recommended && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary" data-testid="help-your-card"><Star className="w-3 h-3" /> yours</span>}</span>
                    <span className="block text-sm text-muted-foreground mt-1">{d.blurb}</span>
                    <span className="block text-xs text-muted-foreground mt-2">{d.available ? `PDF · ${d.size_kb} KB · opens in a new tab` : "Not available yet"}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2">
          <Link to="/walkthrough" className="card-default p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-transform" data-testid="help-walkthrough-link">
            <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Play className="w-5 h-5" /></span>
            <span><span className="font-bold block">Narrated walkthrough</span><span className="text-sm text-muted-foreground">The customer journey, booking to collection, with voice-over. Put it on the big screen for new starters.</span></span>
          </Link>
          <Link to="/how-it-works" className="card-default p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-transform" data-testid="help-how-it-works-link">
            <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Presentation className="w-5 h-5" /></span>
            <span><span className="font-bold block">How it works — slides</span><span className="text-sm text-muted-foreground">The step-by-step tour. Kiosk mode for a TV in reception or the workshop.</span></span>
          </Link>
        </section>

        <p className="text-sm text-muted-foreground" data-testid="help-support-line">Stuck? Steve Kirby · 07885 735028 · Info@servicesellpro.co.uk — Monday to Saturday, 8am–6pm, same-day response.</p>
      </main>
    </div>
  );
}
